import { CLASSES } from '../core/classes.ts';
import { FACTIONS, type ClassId, type Faction } from '../core/constants.ts';
import { SLOT_ORDER, SPELLS_BY_ID, type SlotRole } from '../core/spells.ts';
import {
  BALANCE_SLACK, headcount, maySwitch, memberBadge,
  type LobbyView,
} from '../core/lobby.ts';
import type { ChatMessage } from '../core/chat.ts';
import { openGrimoire } from './grimoire.ts';

/**
 * The room, before and between matches.
 *
 * Three columns across the top because there are three sides and a player's
 * first question is which of them needs them — a single list sorted by faction
 * answers that far worse than three lists do, because the *shape* of the answer
 * is a comparison of heights.
 *
 * Chat is directly under them and deliberately large. This is the social space
 * of the game: the two minutes between rounds are where somebody says what went
 * wrong, and a chat box treated as an afterthought is a room where nobody
 * speaks.
 *
 * Knows nothing about where any of it comes from. It is handed a view and a set
 * of things a player can do, which is what lets it be looked at long before a
 * server exists to fill it.
 */

export interface LobbyHooks {
  view(): LobbyView;
  chat(): readonly ChatMessage[];

  join(faction: Faction): void;
  addBot(faction: Faction): void;
  chooseClass(cls: ClassId): void;
  /**
   * Takes a new loadout. The screen opens the picker itself, because what a
   * picker *is* is a detail of the interface rather than of the room.
   */
  setLoadout(loadout: string[]): void;
  setReady(on: boolean): void;
  /** What this mage is called. The lobby is now the only place it is asked. */
  rename(name: string): void;
  say(text: string): void;
  leave(): void;

  /** The loadout as it stands, by slot. */
  loadout(): readonly string[];
}

const el = <T extends HTMLElement>(tag: string, cls?: string, text?: string): T => {
  const node = document.createElement(tag) as T;
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

const escapeHtml = (raw: string): string =>
  raw.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!
  ));

/** What each slot is for, spelled out beside the spell filling it. */
const SLOT_NAME: Record<SlotRole, string> = {
  innate: 'Innate',
  primary: 'Primary',
  secondary: 'Secondary',
  area: 'Area',
  control: 'Control',
  utility: 'Utility',
  defense: 'Defense',
};

/** `1:07`, which is the only way this screen shows time. */
function mmss(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function runLobbyScreen(root: HTMLElement, hooks: LobbyHooks): { refresh(): void } {
  root.hidden = false;
  root.innerHTML = '';
  root.classList.add('lobby-screen');

  // ── Header ─────────────────────────────────────────────────────────────────
  const title = el('h1', 'lobby-title');

  /*
   * Your name, here rather than on a screen before this one.
   *
   * The muster used to ask for it and the muster is gone — creating a lobby now
   * goes straight here, because being asked for a faction and a discipline twice
   * in a row, once in a list and once in a room, is asking the same question
   * twice and answering it in two places.
   */
  const named = el('label', 'lobby-named');
  named.append(el('span', 'lobby-named-label', 'Known as'));
  const nameInput = el<HTMLInputElement>('input', 'lobby-name-input');
  nameInput.type = 'text';
  nameInput.maxLength = 18;
  nameInput.spellcheck = false;
  nameInput.addEventListener('input', () => hooks.rename(nameInput.value.trim() || 'Mage'));
  nameInput.addEventListener('keydown', (ev) => ev.stopPropagation());
  named.append(nameInput);
  const meta = el('div', 'lobby-meta');
  const roundEl = el('span', 'lobby-round');
  const mapEl = el('span', 'lobby-map-now');
  const clockEl = el('span', 'lobby-clock');
  meta.append(roundEl, mapEl, clockEl);

  /*
   * Said once, across the top, above the sides.
   *
   * Above rather than beside, because it changes what every other number on the
   * screen means — the clock, the score and the rosters are all describing a
   * match that is happening rather than one being arranged.
   */
  const banner = el('div', 'lobby-banner');
  banner.hidden = true;

  root.append(title, named, meta, banner);

  // ── Three columns ──────────────────────────────────────────────────────────
  const columns = el('div', 'lobby-columns');
  const sides = new Map<Faction, {
    heading: HTMLElement; list: HTMLElement; bot: HTMLButtonElement; join: HTMLButtonElement;
  }>();

  for (const faction of FACTIONS) {
    const column = el('section', 'lobby-column');
    column.dataset.faction = faction;

    const heading = el('h2', 'lobby-side');
    const list = el('ul', 'lobby-mages');

    const bot = el<HTMLButtonElement>('button', 'lobby-addbot', '[ Add Bot ]');
    bot.addEventListener('click', () => hooks.addBot(faction));

    const join = el<HTMLButtonElement>('button', 'lobby-join');
    join.textContent = `Join ${faction[0]!.toUpperCase()}${faction.slice(1)}`;
    join.dataset.faction = faction;
    join.addEventListener('click', () => hooks.join(faction));

    column.append(heading, list, bot, join);
    columns.append(column);
    sides.set(faction, { heading, list, bot, join });
  }
  root.append(columns);

  // ── Chat ───────────────────────────────────────────────────────────────────
  const chat = el('section', 'lobby-chat');
  const chatLog = el('div', 'lobby-chat-log');
  const chatLine = el<HTMLInputElement>('input', 'lobby-chat-input');
  chatLine.type = 'text';
  chatLine.maxLength = 160;
  chatLine.placeholder = 'Say something';
  chatLine.addEventListener('keydown', (ev) => {
    ev.stopPropagation();
    if (ev.key !== 'Enter') return;
    const text = chatLine.value.trim();
    if (text) hooks.say(text);
    chatLine.value = '';
  });
  chat.append(chatLog, chatLine);
  root.append(chat);

  // ── Loadout and class ──────────────────────────────────────────────────────
  const lower = el('div', 'lobby-lower');

  const spells = el('section', 'lobby-spells');
  const spellsButton = el<HTMLButtonElement>('button', 'lobby-spellbutton', 'Choose Your Spells');
  spellsButton.addEventListener('click', () => {
    const view = hooks.view();
    const me = view.members.find((m) => m.id === view.youId);
    if (!me?.cls) return;
    // The same grimoire the muster uses. Opened over the room and closed back
    // onto it, so the new choices are on the left the moment it goes away.
    openGrimoire(me.cls as ClassId, [...hooks.loadout()], (loadout) => {
      hooks.setLoadout(loadout);
      refresh();
    });
  });
  const slots = el('ol', 'lobby-slots');
  spells.append(spellsButton, slots);

  const classes = el('section', 'lobby-classes');
  const classButtons = new Map<ClassId, HTMLButtonElement>();
  for (const id of ['magician', 'cleric', 'mentalist', 'arcanist'] as ClassId[]) {
    const pick = el<HTMLButtonElement>('button', 'lobby-class', CLASSES[id].name);
    pick.addEventListener('click', () => hooks.chooseClass(id));
    classes.append(pick);
    classButtons.set(id, pick);
  }

  lower.append(spells, classes);
  root.append(lower);

  // ── Footer ─────────────────────────────────────────────────────────────────
  const footer = el('div', 'lobby-footer');
  const ready = el<HTMLButtonElement>('button', 'lobby-ready');
  ready.addEventListener('click', () => {
    const me = hooks.view().members.find((m) => m.id === hooks.view().youId);
    hooks.setReady(!me?.ready);
  });
  const nextEl = el('span', 'lobby-next');
  const leave = el<HTMLButtonElement>('button', 'lobby-leave', 'Leave lobby');
  leave.addEventListener('click', () => hooks.leave());
  footer.append(ready, nextEl, leave);
  root.append(footer);

  // ── Painting it ────────────────────────────────────────────────────────────
  function refresh(): void {
    const view = hooks.view();
    const me = view.members.find((m) => m.id === view.youId) ?? null;
    const heads = headcount(view.members);

    title.textContent = view.name;
    // Only when it is not being typed into, or a redraw eats the cursor.
    if (document.activeElement !== nameInput && me) nameInput.value = me.name;
    roundEl.textContent = `Round ${view.round} of ${view.bestOf}`;
    mapEl.textContent = view.map;
    /*
     * The clock means two different things and has to say which.
     *
     * In a room that has not started it counts down to the start. In one already
     * being fought it is how much of the current round is left — and a joiner
     * shown "0:42 until start" about a match that began ten minutes ago has been
     * told something false about the thing they are deciding on.
     */
    clockEl.textContent = view.inProgress
      ? `${mmss(view.secondsLeft)} left this round`
      : `${mmss(view.secondsLeft)} until start`;

    /*
     * And the room says plainly that it is under way.
     *
     * Joining a match in progress is a different proposition from joining one
     * about to start — you are walking into a round somebody is winning, at the
     * level of whoever is furthest behind, and you should be told so before you
     * pick a side rather than discovering it when the arena loads.
     */
    banner.hidden = !view.inProgress;
    if (view.inProgress) {
      const lead = FACTIONS
        .map((f) => ({ f, won: view.wins[f] }))
        .sort((a, b) => b.won - a.won);
      const top = lead[0]!;
      banner.textContent = top.won > 0 && top.won > lead[1]!.won
        ? `Match in progress — ${top.f[0]!.toUpperCase()}${top.f.slice(1)} leads `
          + `${lead.map((x) => x.won).join('–')}. You will join the round already running.`
        : 'Match in progress. You will join the round already running.';
    }

    for (const faction of FACTIONS) {
      const side = sides.get(faction)!;
      const won = view.wins[faction];
      const name = `${faction[0]!.toUpperCase()}${faction.slice(1)}`;
      // Wins beside the name, so the standing of the set is read in the same
      // glance as who is on which side.
      side.heading.textContent = won > 0
        ? `${name} — ${won} ${won === 1 ? 'Win' : 'Wins'}`
        : name;

      side.list.innerHTML = view.members
        .filter((m) => m.faction === faction)
        .map((m) => (
          `<li class="lobby-mage" data-you="${m.id === view.youId}">`
          + `<span class="mage-tick">${m.ready ? '✓' : ''}</span>`
          + `<span class="mage-name">${escapeHtml(m.name)}</span>`
          // Marked rather than hidden: a lobby of eleven bots and one person is
          // a different room from a full one, and only this says which.
          + `<span class="mage-bot">${m.bot ? 'bot' : ''}</span>`
          + `<span class="mage-badge">${memberBadge(m)}</span>`
          + '</li>'
        ))
        .join('');

      // Only a host may conjure bots, and a persistent server has no host — its
      // bots are the floor its rules set, and nobody in the room decides them.
      side.bot.hidden = !view.isHost;
      side.bot.disabled = view.members.length >= view.maxPlayers;

      /*
       * A side you may not join is greyed rather than hidden.
       *
       * Hiding it would leave a player wondering where a column went; greying it
       * says the side exists and is closed, which is a different and more useful
       * fact. The rule is the slack — the sides may differ by three.
       */
      const same = me?.faction === faction;
      side.join.disabled = same
        || (me ? !maySwitch(heads, me.faction, faction) : false)
        || view.members.length >= view.maxPlayers;
      side.join.title = side.join.disabled && !same
        ? `Sides may differ by ${BALANCE_SLACK}`
        : '';
    }

    // The loadout, by slot, in the order the keys are numbered.
    slots.innerHTML = SLOT_ORDER.map((slot, i) => {
      const id = hooks.loadout()[i];
      const spell = id ? SPELLS_BY_ID[id] : undefined;
      return `<li class="lobby-slot"><span class="slot-key">${i + 1}</span>`
        + `<span class="slot-name">${escapeHtml(spell?.name ?? '—')}</span>`
        + `<span class="slot-role">(${SLOT_NAME[slot]})</span></li>`;
    }).join('');

    for (const [id, button] of classButtons) {
      button.dataset.on = String(me?.cls === id);
    }

    // No class, no spells to choose.
    spellsButton.disabled = !me?.cls;

    ready.textContent = me?.ready ? 'Ready ✓' : 'Ready';
    ready.dataset.on = String(Boolean(me?.ready));
    ready.disabled = !me || me.cls === null;

    nextEl.textContent = view.nextMap ? `Next Map: ${view.nextMap}` : '';

    chatLog.innerHTML = hooks.chat()
      .map((m) => (
        `<div class="lobby-said">`
        + `<span class="said-who" data-faction="${m.faction}">${escapeHtml(m.fromName)}</span>`
        + `: ${escapeHtml(m.text)}</div>`
      ))
      .join('');
    chatLog.scrollTop = chatLog.scrollHeight;
  }

  refresh();
  return { refresh };
}
